
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Edit2, X, MapPin, ChevronDown, Briefcase, User, Phone, Calendar, Clock, Home, FileText, Save, Trash2, AlertCircle, ArrowLeft } from 'lucide-react';
import { CustomDatePicker } from '../ui/CustomDatePicker';
import { CustomTimePicker } from '../ui/CustomTimePicker';
import { ReunionModalProps } from '../../types';
import { createReunion, updateReunion, deleteReunion } from '../../lib/api/reuniones';
import { getPropuestasByReunionId, type Propuesta as DBPropuesta } from '../../lib/api/propuestas';
import { getGestionesByReunionId, type Gestion as DBGestion } from '../../lib/api/gestiones';
import { getMunicipalityId, isMunicipalitiesMapReady, dbPropuestaToFrontend, dbGestionToFrontend } from '../../lib/adapters';
import { getMunicipalityByName } from '../../lib/api/municipalities';
import { getMunicipalityIdStatic } from '../../lib/municipalities-static';
import { formatDateES } from '../../lib/dateUtils';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useToast } from '../../contexts/ToastContext';
import { useIsMobile, useIsTablet } from '../../hooks/useMediaQuery';
import { useClickOutside } from '../../hooks/useClickOutside';
import { FileUploadZone } from '../shared/FileUploadZone';
import { AttachmentsList } from '../shared/AttachmentsList';
import { sanitizeText, sanitizeTextarea, sanitizePhone } from '../../lib/validation/input';
import { logger } from '../../lib/logger';
import { AuditWatermark } from './shared/AuditWatermark';

export const ReunionModal: React.FC<ReunionModalProps> = ({ isOpen, onClose, municipalities, initialDate, initialData, onOpenPropuesta, onOpenGestion, onSave, onSetRefreshFunction }) => {
    const { currentOrg } = useOrganization();
    const toast = useToast();
    const isMobile = useIsMobile();
    const isTablet = useIsTablet();
    const [isEditing, setIsEditing] = useState(!initialData);
    const [selectedMunicipality, setSelectedMunicipality] = useState(initialData?.municipality || '');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [municipalitySearch, setMunicipalitySearch] = useState('');
    
    // Initialize date/time
    const [selectedDate, setSelectedDate] = useState(initialData?.date || initialDate || new Date().toISOString().split('T')[0]);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    
    const [selectedTime, setSelectedTime] = useState(initialData?.time || '');
    const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);
    
    // Other fields
    const [meetingName, setMeetingName] = useState(initialData?.title || '');
    const [organizer, setOrganizer] = useState(initialData?.organizer || '');
    const [phone, setPhone] = useState(initialData?.phone || '');
    const [notes, setNotes] = useState(initialData?.notes || '');
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

    // Attachments refresh trigger
    const [attachmentsRefreshTrigger, setAttachmentsRefreshTrigger] = useState(0);

    // Linked items popup state
    const [showPropuestasPopup, setShowPropuestasPopup] = useState(false);
    const [showGestionesPopup, setShowGestionesPopup] = useState(false);
    const [linkedPropuestas, setLinkedPropuestas] = useState<DBPropuesta[]>([]);
    const [linkedGestiones, setLinkedGestiones] = useState<DBGestion[]>([]);
    const [loadingLinkedItems, setLoadingLinkedItems] = useState(false);

    // Local counts that can be updated without closing the modal
    const [currentPropuestasCount, setCurrentPropuestasCount] = useState(initialData?.propuestasCount ?? 0);
    const [currentGestionesCount, setCurrentGestionesCount] = useState(initialData?.gestionesCount ?? 0);

    const dropdownRef = useClickOutside<HTMLDivElement>(() => setIsDropdownOpen(false));
    const datePickerRef = useClickOutside<HTMLDivElement>(() => setIsDatePickerOpen(false));
    const timePickerRef = useClickOutside<HTMLDivElement>(() => setIsTimePickerOpen(false));
    const propuestasPopupRef = useClickOutside<HTMLDivElement>(() => setShowPropuestasPopup(false));
    const gestionesPopupRef = useClickOutside<HTMLDivElement>(() => setShowGestionesPopup(false));

    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                // Load existing data
                setIsEditing(false);
                setSelectedMunicipality(initialData.municipality || '');
                setSelectedDate(initialData.date || '');
                setSelectedTime(initialData.time || '');
                setMeetingName(initialData.title || '');
                setOrganizer(initialData.organizer || '');
                setPhone(initialData.phone || '');
                setNotes(initialData.notes || '');
                // Reset counts from initialData
                setCurrentPropuestasCount(initialData.propuestasCount ?? 0);
                setCurrentGestionesCount(initialData.gestionesCount ?? 0);
            } else {
                // Reset for new entry
                setIsEditing(true);
                // Keep default date if passed
                if (initialDate) setSelectedDate(initialDate);
            }
        }
    }, [isOpen, initialData, initialDate]);

    // Register refresh function with parent component
    useEffect(() => {
        if (isOpen && onSetRefreshFunction) {
            onSetRefreshFunction(refreshLinkedItemsCounts);
        }
        // Cleanup: unregister when modal closes
        return () => {
            if (onSetRefreshFunction) {
                onSetRefreshFunction(async () => {});
            }
        };
    }, [isOpen, onSetRefreshFunction]);

    // Load linked propuestas when popup opens
    const handlePropuestasCardClick = async () => {
        if (!initialData?.id) return;
        setLoadingLinkedItems(true);
        try {
            const propuestas = await getPropuestasByReunionId(initialData.id);
            setLinkedPropuestas(propuestas);
            setShowPropuestasPopup(true);
        } catch (error) {
            logger.error('Error loading linked propuestas:', error);
        } finally {
            setLoadingLinkedItems(false);
        }
    };

    // Load linked gestiones when popup opens
    const handleGestionesCardClick = async () => {
        if (!initialData?.id) return;
        setLoadingLinkedItems(true);
        try {
            const gestiones = await getGestionesByReunionId(initialData.id);
            setLinkedGestiones(gestiones);
            setShowGestionesPopup(true);
        } catch (error) {
            logger.error('Error loading linked gestiones:', error);
        } finally {
            setLoadingLinkedItems(false);
        }
    };

    // Refresh linked items counts (called after creating new propuesta/gestion)
    const refreshLinkedItemsCounts = async () => {
        if (!initialData?.id) return;
        logger.log('[ReunionModal] Refreshing linked items counts for reunion:', initialData.id);
        try {
            const propuestas = await getPropuestasByReunionId(initialData.id);
            const gestiones = await getGestionesByReunionId(initialData.id);
            setCurrentPropuestasCount(propuestas.length);
            setCurrentGestionesCount(gestiones.length);
            logger.log('[ReunionModal] Updated counts - Propuestas:', propuestas.length, 'Gestiones:', gestiones.length);
        } catch (error) {
            logger.error('[ReunionModal] Error refreshing counts:', error);
        }
    };

    const filteredMunicipalities = municipalities.filter(m =>
        m.toLowerCase().includes(municipalitySearch.toLowerCase())
    );

    const getFormattedDate = (dateString: string) => {
        if (!dateString) return '';
        return formatDateES(dateString, { format: 'short', includeWeekday: true });
    };

    const getFormattedTime = (timeString: string) => {
        if (!timeString) return '';
        const [h, m] = timeString.split(':');
        const hour = parseInt(h);
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${m} ${period}`;
    };

    const handleDelete = async () => {
        if (!initialData || !initialData.id) {
            setIsDeleteConfirmOpen(false);
            return;
        }

        try {
            await deleteReunion(initialData.id as unknown as string);
            setIsDeleteConfirmOpen(false);
            if (onSave) {
                onSave();
            }
            onClose();
        } catch (error) {
            logger.error('[ReunionModal] Error deleting reunion:', error);
            toast.error('Error al eliminar', 'No se pudo eliminar la reunión. Por favor intenta de nuevo.');
            setIsDeleteConfirmOpen(false);
        }
    };

    if (!isOpen) return null;

    // Progressive modal sizing: mobile (full), tablet (90% width, max-w-xl), desktop (max-w-2xl)
    const getModalContainerPadding = () => {
        if (isMobile) return 'p-0';
        if (isTablet) return 'p-3';
        return 'p-4';
    };

    const getModalContentClass = () => {
        if (isMobile) return 'w-full h-full';
        if (isTablet) return 'rounded-xl w-[92%] max-w-xl max-h-[88vh]';
        return 'rounded-xl w-full max-w-2xl max-h-[90vh]';
    };

    return (
        <div className={`fixed inset-0 z-[110] flex items-center justify-center ${getModalContainerPadding()}`}>
            <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={onClose}
                className="absolute inset-0 bg-[#111827]/60 backdrop-blur-sm"
            />

            <motion.div
                initial={{ opacity: 0, scale: isMobile ? 1 : 0.95, y: isMobile ? 0 : 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: isMobile ? 1 : 0.95, y: isMobile ? 0 : 20 }}
                onClick={(e) => e.stopPropagation()}
                className={`bg-white shadow-2xl relative z-10 overflow-hidden flex flex-col border border-[#D1D5DB] ${getModalContentClass()}`}
            >
                {/* Header */}
                <div className="px-8 py-6 border-b border-[#D1D5DB] flex items-center justify-between bg-[#F9FAFB]">
                    <div>
                        <h2 className="text-xl font-bold text-[#111827] flex items-center gap-2">
                            <Users className="text-[#5D1828]" size={24} />
                            {initialData ? 'Detalles de la Reunión' : 'Nueva Reunión'}
                        </h2>
                        <p className="text-sm text-[#6B7280] mt-1">
                            {isEditing ? 'Capture los detalles de la nueva reunión.' : 'Información registrada del evento.'}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {initialData && !isEditing && (
                            <>
                                <button
                                    onClick={() => setIsDeleteConfirmOpen(true)}
                                    className="p-2 hover:bg-red-50 rounded-full text-[#DC2626] hover:text-[#B91C1C] transition-colors"
                                    title="Eliminar"
                                >
                                    <Trash2 size={18} />
                                </button>
                                <button onClick={() => setIsEditing(true)} className="p-2 hover:bg-[#E5E7EB] rounded-full text-[#6B7280] hover:text-[#111827] transition-colors" title="Editar">
                                    <Edit2 size={18} />
                                </button>
                            </>
                        )}
                        <button onClick={onClose} className="p-2 hover:bg-[#E5E7EB] rounded-full text-[#6B7280] hover:text-[#111827] transition-colors">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="p-4 md:p-8 overflow-y-auto custom-scrollbar">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
                        
                        {/* Municipality */}
                        <div className="col-span-2 lg:col-span-1">
                            <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">Municipio {isEditing && <span className="text-[#5D1828]">*</span>}</label>
                            {isEditing ? (
                                <div className="relative" ref={dropdownRef}>
                                    <button 
                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                        className={`w-full flex items-center justify-between px-4 py-3 bg-white border rounded-lg text-left transition-all ${isDropdownOpen ? 'border-[#5D1828] ring-1 ring-[#5D1828]' : 'border-[#D1D5DB] hover:border-[#9CA3AF]'}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <MapPin size={18} className="text-[#6B7280]" />
                                            <span className={`text-sm ${selectedMunicipality ? 'text-[#111827] font-medium' : 'text-[#6B7280]'}`}>
                                                {selectedMunicipality || 'Seleccionar municipio...'}
                                            </span>
                                        </div>
                                        <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    <AnimatePresence>
                                        {isDropdownOpen && (
                                            <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 5 }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-[#D1D5DB] overflow-hidden z-50 max-h-60 flex flex-col">
                                                <div className="p-2 border-b border-[#E5E7EB] bg-[#F9FAFB]">
                                                    <input type="text" autoFocus placeholder="Buscar..." value={municipalitySearch} onChange={(e) => setMunicipalitySearch(e.target.value)} className="w-full bg-white border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#5D1828]" />
                                                </div>
                                                <div className="overflow-y-auto p-1">
                                                    {filteredMunicipalities.map((city) => (
                                                        <button key={city} onClick={() => { setSelectedMunicipality(city); setIsDropdownOpen(false); setMunicipalitySearch(''); }} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${selectedMunicipality === city ? 'bg-[#5D1828]/10 text-[#5D1828] font-medium' : 'text-[#4B5563] hover:bg-[#F3F4F6]'}`}>{city}</button>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ) : (
                                <div className="text-[#111827] font-medium flex items-center gap-2 p-3 bg-[#F9FAFB] rounded-lg border border-[#D1D5DB]">
                                    <MapPin size={18} className="text-[#6B7280]" /> {selectedMunicipality}
                                </div>
                            )}
                        </div>

                        {/* Meeting Name */}
                        <div className="col-span-2 lg:col-span-1">
                             <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">Nombre de la Reunión {isEditing && <span className="text-[#5D1828]">*</span>}</label>
                             <div className="relative">
                                {isEditing ? (
                                    <>
                                        <Briefcase size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                                        <input type="text" value={meetingName} onChange={(e) => setMeetingName(sanitizeText(e.target.value, 200))} placeholder="Ej. Asamblea Vecinal" className="w-full pl-11 pr-4 py-3 bg-white border border-[#D1D5DB] rounded-lg text-[#111827] text-sm outline-none focus:border-[#5D1828] focus:ring-1 focus:ring-[#5D1828] transition-all placeholder:text-[#9CA3AF]" maxLength={200} />
                                    </>
                                ) : (
                                    <div className="text-[#111827] font-medium flex items-center gap-2 p-3 bg-[#F9FAFB] rounded-lg border border-[#D1D5DB]">
                                        <Briefcase size={18} className="text-[#6B7280]" /> {meetingName}
                                    </div>
                                )}
                             </div>
                        </div>

                         {/* Organizer Name */}
                         <div className="col-span-2 lg:col-span-1">
                             <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">Organizador {isEditing && <span className="text-[#5D1828]">*</span>}</label>
                             <div className="relative">
                                {isEditing ? (
                                    <>
                                        <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                                        <input type="text" value={organizer} onChange={(e) => setOrganizer(sanitizeText(e.target.value, 200))} placeholder="Nombre completo" className="w-full pl-11 pr-4 py-3 bg-white border border-[#D1D5DB] rounded-lg text-[#111827] text-sm outline-none focus:border-[#5D1828] focus:ring-1 focus:ring-[#5D1828] transition-all placeholder:text-[#9CA3AF]" maxLength={200} />
                                    </>
                                ) : (
                                    <div className="text-[#111827] font-medium flex items-center gap-2 p-3 bg-[#F9FAFB] rounded-lg border border-[#D1D5DB]">
                                        <User size={18} className="text-[#6B7280]" /> {organizer}
                                    </div>
                                )}
                             </div>
                        </div>

                        {/* Organizer Phone */}
                        <div className="col-span-2 lg:col-span-1">
                             <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">Teléfono {isEditing && <span className="text-[#5D1828]">*</span>}</label>
                             <div className="relative">
                                {isEditing ? (
                                    <>
                                        <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                                        <input type="tel" value={phone} onChange={(e) => setPhone(sanitizePhone(e.target.value))} placeholder="(614) 000-0000" className="w-full pl-11 pr-4 py-3 bg-white border border-[#D1D5DB] rounded-lg text-[#111827] text-sm outline-none focus:border-[#5D1828] focus:ring-1 focus:ring-[#5D1828] transition-all placeholder:text-[#9CA3AF]" maxLength={20} />
                                    </>
                                ) : (
                                    <div className="text-[#111827] font-medium flex items-center gap-2 p-3 bg-[#F9FAFB] rounded-lg border border-[#D1D5DB]">
                                        <Phone size={18} className="text-[#6B7280]" /> {phone}
                                    </div>
                                )}
                             </div>
                        </div>

                        {/* Date Picker */}
                        <div className="col-span-2 lg:col-span-1">
                            <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">Fecha {isEditing && <span className="text-[#5D1828]">*</span>}</label>
                            {isEditing ? (
                                <div className="relative" ref={datePickerRef}>
                                    <button onClick={() => setIsDatePickerOpen(!isDatePickerOpen)} className={`relative w-full py-3 px-4 rounded-lg border-2 border-dashed transition-all flex items-center justify-center gap-3 group select-none ${selectedDate ? 'bg-[#5D1828]/5 border-[#5D1828]/30 text-[#5D1828]' : 'bg-[#F9FAFB] border-[#D1D5DB] hover:border-[#5D1828] hover:bg-white text-[#6B7280] hover:text-[#5D1828]'}`}>
                                        <Calendar size={20} className={selectedDate ? 'text-[#5D1828]' : ''} />
                                        <span className={`font-medium text-sm ${selectedDate ? 'text-[#111827]' : ''}`}>{selectedDate ? getFormattedDate(selectedDate) : 'Seleccionar fecha'}</span>
                                    </button>
                                    <AnimatePresence>{isDatePickerOpen && <CustomDatePicker selectedDate={selectedDate} onChange={setSelectedDate} onClose={() => setIsDatePickerOpen(false)} parentRef={datePickerRef} />}</AnimatePresence>
                                </div>
                            ) : (
                                <div className="text-[#111827] font-medium flex items-center gap-2 p-3 bg-[#F9FAFB] rounded-lg border border-[#D1D5DB]">
                                    <Calendar size={18} className="text-[#6B7280]" /> {getFormattedDate(selectedDate)}
                                </div>
                            )}
                        </div>

                        {/* Time Picker */}
                        <div className="col-span-2 lg:col-span-1">
                            <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">Hora {isEditing && <span className="text-[#5D1828]">*</span>}</label>
                            {isEditing ? (
                                <div className="relative" ref={timePickerRef}>
                                    <button onClick={() => setIsTimePickerOpen(!isTimePickerOpen)} className={`relative w-full py-3 px-4 rounded-lg border-2 border-dashed transition-all flex items-center justify-center gap-3 group select-none ${selectedTime ? 'bg-[#B89658]/10 border-[#B89658]/30 text-[#B89658]' : 'bg-[#F9FAFB] border-[#D1D5DB] hover:border-[#B89658] hover:bg-white text-[#6B7280] hover:text-[#B89658]'}`}>
                                        <Clock size={20} className={selectedTime ? 'text-[#B89658]' : ''} />
                                        <span className={`font-medium text-sm ${selectedTime ? 'text-[#111827]' : ''}`}>{selectedTime ? getFormattedTime(selectedTime) : 'Definir hora'}</span>
                                    </button>
                                    <AnimatePresence>{isTimePickerOpen && <CustomTimePicker selectedTime={selectedTime} onChange={setSelectedTime} onClose={() => setIsTimePickerOpen(false)} />}</AnimatePresence>
                                </div>
                            ) : (
                                <div className="text-[#111827] font-medium flex items-center gap-2 p-3 bg-[#F9FAFB] rounded-lg border border-[#D1D5DB]">
                                    <Clock size={18} className="text-[#6B7280]" /> {getFormattedTime(selectedTime)}
                                </div>
                            )}
                        </div>

                        {/* Linked Items Display (only when viewing existing reunion) */}
                        {!isEditing && initialData && initialData.id && (currentPropuestasCount > 0 || currentGestionesCount > 0) ? (
                            <div className="col-span-2">
                                <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-3">Items Vinculados</label>
                                <div className="grid grid-cols-2 gap-3 md:gap-4">
                                    {currentPropuestasCount > 0 && (
                                        <div className="relative">
                                            <button
                                                onClick={handlePropuestasCardClick}
                                                disabled={loadingLinkedItems}
                                                className="w-full bg-[#5D1828]/5 border border-[#5D1828]/20 rounded-lg p-3 md:p-4 hover:bg-[#5D1828]/10 hover:border-[#5D1828]/30 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-wait"
                                            >
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Home size={16} className="text-[#5D1828]" />
                                                    <span className="text-xs font-semibold text-[#4B5563] uppercase tracking-wide">Propuestas</span>
                                                </div>
                                                <div className="text-2xl font-black text-[#5D1828]">
                                                    {currentPropuestasCount}
                                                </div>
                                                <p className="text-[10px] text-[#6B7280] mt-1">
                                                    {loadingLinkedItems ? 'Cargando...' : 'Clic para ver'}
                                                </p>
                                            </button>

                                            {/* Propuestas Popup Menu */}
                                            <AnimatePresence>
                                                {showPropuestasPopup && (
                                                    <motion.div
                                                        ref={propuestasPopupRef}
                                                        initial={{ opacity: 0, y: -10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, y: -10 }}
                                                        className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-[#D1D5DB] overflow-hidden z-50 max-h-64"
                                                    >
                                                        <div className="px-3 py-2 bg-[#5D1828]/5 border-b border-[#D1D5DB]">
                                                            <span className="text-xs font-semibold text-[#5D1828] uppercase tracking-wide">Seleccionar Propuesta</span>
                                                        </div>
                                                        <div className="overflow-y-auto max-h-52">
                                                            {linkedPropuestas.length > 0 ? (
                                                                linkedPropuestas.map((propuesta) => (
                                                                    <button
                                                                        key={propuesta.id}
                                                                        onClick={() => {
                                                                            onOpenPropuesta();
                                                                            setShowPropuestasPopup(false);
                                                                            // Convert database record to frontend format before passing
                                                                            setTimeout(() => {
                                                                                const frontendPropuesta = dbPropuestaToFrontend(propuesta);
                                                                                const event = new CustomEvent('openPropuestaDetails', { detail: frontendPropuesta });
                                                                                window.dispatchEvent(event);
                                                                            }, 100);
                                                                        }}
                                                                        className="w-full text-left px-4 py-3 hover:bg-[#5D1828]/5 transition-colors border-b border-[#E5E7EB] last:border-b-0"
                                                                    >
                                                                        <div className="font-semibold text-[#111827] text-sm">{propuesta.folio}</div>
                                                                        <div className="text-xs text-[#6B7280] mt-0.5 truncate">{propuesta.proponent}</div>
                                                                    </button>
                                                                ))
                                                            ) : (
                                                                <div className="px-4 py-6 text-center text-sm text-[#6B7280]">
                                                                    No se encontraron propuestas
                                                                </div>
                                                            )}
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    )}
                                    {currentGestionesCount > 0 && (
                                        <div className="relative">
                                            <button
                                                onClick={handleGestionesCardClick}
                                                disabled={loadingLinkedItems}
                                                className="w-full bg-[#B89658]/5 border border-[#B89658]/20 rounded-lg p-3 md:p-4 hover:bg-[#B89658]/10 hover:border-[#B89658]/30 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-wait"
                                            >
                                                <div className="flex items-center gap-2 mb-1">
                                                    <FileText size={16} className="text-[#B89658]" />
                                                    <span className="text-xs font-semibold text-[#4B5563] uppercase tracking-wide">Gestiones</span>
                                                </div>
                                                <div className="text-2xl font-black text-[#B89658]">
                                                    {currentGestionesCount}
                                                </div>
                                                <p className="text-[10px] text-[#6B7280] mt-1">
                                                    {loadingLinkedItems ? 'Cargando...' : 'Clic para ver'}
                                                </p>
                                            </button>

                                            {/* Gestiones Popup Menu */}
                                            <AnimatePresence>
                                                {showGestionesPopup && (
                                                    <motion.div
                                                        ref={gestionesPopupRef}
                                                        initial={{ opacity: 0, y: -10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, y: -10 }}
                                                        className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-[#D1D5DB] overflow-hidden z-50 max-h-64"
                                                    >
                                                        <div className="px-3 py-2 bg-[#B89658]/5 border-b border-[#D1D5DB]">
                                                            <span className="text-xs font-semibold text-[#B89658] uppercase tracking-wide">Seleccionar Gestión</span>
                                                        </div>
                                                        <div className="overflow-y-auto max-h-52">
                                                            {linkedGestiones.length > 0 ? (
                                                                linkedGestiones.map((gestion) => (
                                                                    <button
                                                                        key={gestion.id}
                                                                        onClick={() => {
                                                                            onOpenGestion();
                                                                            setShowGestionesPopup(false);
                                                                            // Convert database record to frontend format before passing
                                                                            setTimeout(() => {
                                                                                const frontendGestion = dbGestionToFrontend(gestion);
                                                                                const event = new CustomEvent('openGestionDetails', { detail: frontendGestion });
                                                                                window.dispatchEvent(event);
                                                                            }, 100);
                                                                        }}
                                                                        className="w-full text-left px-4 py-3 hover:bg-[#B89658]/5 transition-colors border-b border-[#E5E7EB] last:border-b-0"
                                                                    >
                                                                        <div className="font-semibold text-[#111827] text-sm">{gestion.folio}</div>
                                                                        <div className="text-xs text-[#6B7280] mt-0.5 truncate">{gestion.solicitant}</div>
                                                                    </button>
                                                                ))
                                                            ) : (
                                                                <div className="px-4 py-6 text-center text-sm text-[#6B7280]">
                                                                    No se encontraron gestiones
                                                                </div>
                                                            )}
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : null}

                        {/* Action Buttons Section (Always visible for quick actions) */}
                        <div className="col-span-2 pt-2">
                             <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-3">Acciones Rápidas</label>
                             <div className="flex gap-4">
                                <button
                                    onClick={() => { /* Don't close reunion, just open proposal on top */ onOpenPropuesta(); }}
                                    className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border border-dashed border-[#5D1828]/30 bg-[#5D1828]/5 rounded-lg text-[#5D1828] font-medium hover:bg-[#5D1828]/10 transition-colors"
                                >
                                    <Home size={18} />
                                    <span>+ Propuesta</span>
                                </button>
                                <button
                                    onClick={() => { /* Don't close reunion */ onOpenGestion(); }}
                                    className="flex-1 flex items-center justify-center gap-2 py-3 px-4 border border-dashed border-[#B89658]/30 bg-[#B89658]/5 rounded-lg text-[#B89658] font-medium hover:bg-[#B89658]/10 transition-colors"
                                >
                                    <FileText size={18} />
                                    <span>+ Gestión</span>
                                </button>
                             </div>
                        </div>

                        {/* Notes Section */}
                        <div className="col-span-2">
                             <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">Notas Adicionales</label>
                             <div className="relative">
                                {isEditing ? (
                                    <textarea rows={4} value={notes} onChange={(e) => setNotes(sanitizeTextarea(e.target.value, 5000))} placeholder="Escribe aquí cualquier detalle importante..." className="w-full p-4 bg-white border border-[#D1D5DB] rounded-lg text-[#111827] text-base outline-none focus:border-[#5D1828] focus:ring-1 focus:ring-[#5D1828] transition-all placeholder:text-[#9CA3AF] resize-none" maxLength={5000} />
                                ) : (
                                    <div className="w-full p-4 bg-[#F9FAFB] border border-[#D1D5DB] rounded-lg text-[#111827] text-sm min-h-[100px] whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                                        {notes || 'Sin notas adicionales.'}
                                    </div>
                                )}
                             </div>
                        </div>

                        {/* Attachments Section */}
                        {initialData && initialData.id ? (
                            <>
                                {/* File Upload Zone */}
                                <div className="col-span-2">
                                    <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">Subir Archivos</label>
                                    <FileUploadZone
                                        entityType="reunion"
                                        entityId={initialData.id}
                                        onUploadComplete={() => setAttachmentsRefreshTrigger(prev => prev + 1)}
                                        disabled={false}
                                    />
                                </div>

                                {/* Attachments List */}
                                <div className="col-span-2">
                                    <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">Archivos Adjuntos</label>
                                    <AttachmentsList
                                        entityType="reunion"
                                        entityId={initialData.id}
                                        onAttachmentDeleted={() => setAttachmentsRefreshTrigger(prev => prev + 1)}
                                        refreshTrigger={attachmentsRefreshTrigger}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="col-span-2">
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                                    <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <div>
                                        <p className="text-sm font-medium text-blue-900">Archivos disponibles después de guardar</p>
                                        <p className="text-sm text-blue-700 mt-1">Guarda esta reunión primero para poder adjuntar archivos.</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Audit Watermark (view mode only) */}
                        {!isEditing && initialData && (
                            <AuditWatermark
                                createdBy={initialData.created_by}
                                createdAt={initialData.created_at}
                                confirmedBy={initialData.confirmed_by}
                                realizedBy={initialData.realized_by}
                                entityType="reunion"
                            />
                        )}

                    </div>
                </div>

                {/* Footer */}
                <div className="px-4 md:px-8 py-4 md:py-5 border-t border-[#D1D5DB] bg-[#F9FAFB] flex items-center justify-end gap-2 md:gap-3">
                    <button onClick={onClose} className="px-6 py-3 md:py-2.5 min-h-[44px] rounded-lg text-[#4B5563] font-medium hover:bg-white hover:shadow-sm border border-transparent hover:border-[#D1D5DB] transition-all text-sm">
                        Cerrar
                    </button>
                    {isEditing && (
                        <button type="button" onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();

                            if (!selectedMunicipality) {
                                toast.warning('Campos incompletos', 'Por favor selecciona un municipio');
                                return;
                            }

                            if (!meetingName || !organizer || !phone || !selectedDate || !selectedTime) {
                                toast.warning('Campos incompletos', 'Por favor completa todos los campos requeridos');
                                return;
                            }

                            // Try cache first, then static fallback, then query Supabase
                            let municipalityId = getMunicipalityId(selectedMunicipality);
                            if (!municipalityId) {
                                municipalityId = getMunicipalityIdStatic(selectedMunicipality);
                            }
                            if (!municipalityId) {
                                try {
                                    const municipality = await getMunicipalityByName(selectedMunicipality);
                                    municipalityId = municipality?.id || null;
                                } catch (err) {
                                    logger.warn('[ReunionModal] Municipality lookup failed', err);
                                }
                            }

                            if (!municipalityId) {
                                toast.error('Error de datos', 'No se pudo encontrar el municipio. Verifica la conexión.');
                                return;
                            }

                            if (!currentOrg?.id) {
                                toast.error('Error de sesión', 'No se pudo obtener la organización actual');
                                return;
                            }

                            // Normalize time to HH:MM:SS format
                            // selectedTime can be HH:MM (from picker) or HH:MM:SS (from database)
                            const timeParts = selectedTime.split(':');
                            const normalizedTime = timeParts.length === 2
                                ? `${timeParts[0]}:${timeParts[1]}:00`  // HH:MM -> HH:MM:00
                                : `${timeParts[0]}:${timeParts[1]}:${timeParts[2]}`; // Keep HH:MM:SS

                            const reunionData = {
                                title: meetingName,
                                municipality_id: municipalityId,
                                organization_id: currentOrg.id,
                                date: selectedDate,
                                time: normalizedTime,
                                status: 'Programada' as const,
                                organizer: organizer,
                                organizer_phone: phone,
                                location: null,
                                description: null,
                                notes: notes || null,
                            };

                            try {
                                if (initialData && initialData.id) {
                                    await updateReunion(initialData.id as unknown as string, reunionData);
                                } else {
                                    await createReunion(reunionData);
                                }
                                setIsEditing(false);
                                if (onSave) {
                                    onSave();
                                }
                                onClose();
                            } catch (error) {
                                logger.error('Error saving reunion:', error);
                                toast.error('Error al guardar', 'No se pudo guardar la reunión. Por favor intenta de nuevo.');
                            }
                        }} className="px-6 py-3 md:py-2.5 min-h-[44px] bg-[#5D1828] text-white rounded-lg font-medium hover:bg-[#7A2235] shadow-lg shadow-[#5D1828]/40 transition-all flex items-center gap-2 text-sm" style={{ zIndex: 9999, position: 'relative' }}>
                            <Save size={18} />
                            Guardar Cambios
                        </button>
                    )}
                </div>
            </motion.div>

            {/* Delete Confirmation Modal */}
            <AnimatePresence>
                {isDeleteConfirmOpen && (
                    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
                         <motion.div
                             initial={{ opacity: 0 }}
                             animate={{ opacity: 1 }}
                             exit={{ opacity: 0 }}
                             onClick={() => setIsDeleteConfirmOpen(false)}
                             className="absolute inset-0 bg-[#111827]/60 backdrop-blur-sm"
                         />
                         <motion.div
                            initial={{ opacity: 0, scale: 0.9, y: 10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 10 }}
                            className="bg-white rounded-xl shadow-2xl w-full max-w-sm relative z-50 p-6 flex flex-col items-center text-center"
                         >
                            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4 text-[#DC2626]">
                                <AlertCircle size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-[#111827] mb-2">¿Eliminar reunión?</h3>
                            <p className="text-[#6B7280] text-sm mb-6">
                                Esta acción no se puede deshacer. La reunión y todas sus vinculaciones serán eliminadas permanentemente del sistema.
                            </p>
                            <div className="flex w-full gap-3">
                                <button
                                    onClick={() => setIsDeleteConfirmOpen(false)}
                                    className="flex-1 px-4 py-2.5 rounded-lg border border-[#D1D5DB] text-[#4B5563] font-medium hover:bg-[#F9FAFB] transition-colors text-sm"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleDelete}
                                    className="flex-1 px-4 py-2.5 rounded-lg bg-[#DC2626] text-white font-medium hover:bg-[#B91C1C] shadow-md shadow-[#DC2626]/30 transition-all text-sm"
                                >
                                    Eliminar
                                </button>
                            </div>
                         </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};